import React, { useState, useEffect } from 'react';
import './DashboardPage.css';
import { useAuth } from './AuthContext';
import confetti from 'canvas-confetti';

interface DashboardCounts {
  directReferrals: number;
  walletBalance: number;
  targetName: string | null;
  totalDirectCredit: number;
  totalCreditSum: number;
}
interface Transaction {
  id: number;
  date: string;
  transaction_date?: string;
  amount: number;
  totalReceivedAmount?: number;
  receivedAmountCount?: number;
  status: string;
  type: string;
}

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  // State for dashboard stats
  const [counts, setCounts] = useState<DashboardCounts | null>(null);
  // State for credit transaction table
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  // State management for different parts of the page
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [isTransactionsLoading, setIsTransactionsLoading] = useState(true);
  const [transactionsError, setTransactionsError] = useState<string | null>(null);

  // Pagination state for the transactions table
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [goToPage, setGoToPage] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // Admin "View As" state
  const [viewAsUser, setViewAsUser] = useState<string>('');
  const [viewAsSearchTerm, setViewAsSearchTerm] = useState<string>('');
  const [verificationMessage, setVerificationMessage] = useState<{ status: 'success' | 'error'; message: string } | null>(null);
  const [showYesterdayModal, setShowYesterdayModal] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsStatsLoading(true);
      setStatsError(null);
      try {
        // Fetch both counts and members data in parallel
        const params = new URLSearchParams();
        if (user?.role === 'admin' && viewAsUser) {
          params.append('username', viewAsUser);
        }
        
        const [countsResponse, summaryResponse] = await Promise.all([
          fetch(`/api/dashboard/counts?${params.toString()}`, { credentials: 'include' }),
          fetch(`/api/wallet/summary?${params.toString()}`, { credentials: 'include' })
        ]);
        
        if (!countsResponse.ok) {
          const errorData = await countsResponse.json();
          if (countsResponse.status === 404) {
            setCounts(null); // Explicitly set to null if user not found
            setStatsError(`User "${viewAsUser}" not found.`);
            return; // Stop execution if the primary user data is not found
          }
          throw new Error(errorData.message || 'Failed to fetch dashboard counts');
        }

        if (!summaryResponse.ok) {
          const errorData = await summaryResponse.json();
          throw new Error(errorData.message || 'Failed to fetch wallet summary');
        }

        const countsData: DashboardCounts = await countsResponse.json();
        const summaryData = await summaryResponse.json();

        const defaultCounts: DashboardCounts = {
          directReferrals: 0,
          walletBalance: 0,
          targetName: null,
          totalDirectCredit: 0,
          totalCreditSum: 0,
        };

        setCounts({ ...defaultCounts, ...countsData, ...summaryData.summary });

      } catch (err) {
        if (err instanceof Error) {
          setStatsError(err.message);
        } else {
          setStatsError('An unknown error occurred while fetching stats.');
        }
      } finally {
        setIsStatsLoading(false);
      }
    };

    fetchDashboardData();
  }, [viewAsUser, user?.role]);

  useEffect(() => {
    const fetchTransactions = async () => {
      setIsTransactionsLoading(true);
      setTransactionsError(null);
      try {
        const params = new URLSearchParams({
          page: String(currentPage),
          limit: String(itemsPerPage),
          type: 'credit',
        });
        if (user?.role === 'admin' && viewAsUser) {
          params.append('username', viewAsUser);
        }
        if (searchTerm) {
          params.append('searchTerm', searchTerm);
        }

        const transactionsResponse = await fetch(`/api/dashboard/transactions?${params.toString()}`, { credentials: 'include' });
        if (!transactionsResponse.ok) {
          const errorData = await transactionsResponse.json();
          throw new Error(errorData.message || 'Failed to fetch transactions');
        }
        const transactionsPayload = await transactionsResponse.json();
        setTransactions(transactionsPayload.transactions);
        setTotalPages(Math.max(1, Math.ceil((transactionsPayload.total || 0) / itemsPerPage)));
      } catch (err) {
        if (err instanceof Error) {
          setTransactionsError(err.message);
        } else {
          setTransactionsError('An unknown error occurred while fetching transactions.');
        }
      } finally {
        setIsTransactionsLoading(false);
      }
    };

    fetchTransactions();
  }, [currentPage, itemsPerPage, searchTerm, viewAsUser, user?.role]);

  // Reset to page 1 when filters or items per page change
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage, searchTerm, viewAsUser]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleGoToPageSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const page = parseInt(goToPage, 10);
    if (!isNaN(page)) {
      handlePageChange(page);
    }
    setGoToPage('');
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(searchInput);
  };

  const formatTransactionDate = (dateValue: string, fallbackDate?: string) => {
    const rawValue = dateValue || fallbackDate || '';
    if (!rawValue) return 'N/A';
    const date = new Date(rawValue.replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) {
      return rawValue;
    }
    return date.toLocaleString();
  };

  const handleViewAsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const usernameToView = viewAsSearchTerm.trim();
    if (!usernameToView) {
      setVerificationMessage({ status: 'error', message: 'Please enter a username to view.' });
      return;
    }

    // Reset previous messages and user view
    setVerificationMessage(null);

    try {
      // 1. Verify user exists
      const response = await fetch(`/api/profile?username=${encodeURIComponent(usernameToView)}`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'User not found.');
      }
      // 2. If successful, switch the view
      setViewAsUser(usernameToView);
    } catch (err) {
      // 3. If verification fails, show an error and clear any existing "view as" user
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setVerificationMessage({ status: 'error', message: errorMessage });
      setViewAsUser('');
    }
  };

  const handleClearViewAs = () => {
    setViewAsUser('');
    setViewAsSearchTerm('');
    setVerificationMessage(null);
    setStatsError(null);
  };


  const renderTransactionsTable = () => {
    if (isTransactionsLoading) return (
      <div className="self-members-card">
        <h3>Credit Transactions</h3>
        <p>Loading list...</p>
      </div>
    );

    if (transactionsError) return (
      <div className="self-members-card error-message">
        <h3>Credit Transactions</h3>
        <p>{transactionsError}</p>
      </div>
    );

    return (
      <div className="self-members-card">
        <h3 className="table-header-with-count">
          <span className="header-title">Credit Transactions</span>
          {counts && <span className="table-income">Total Credit: <strong>₹{counts.totalCreditSum.toFixed(2)}</strong></span>}
          <form onSubmit={handleSearchSubmit} className="header-search-form">
            <input
              type="text"
              placeholder="Search transactions..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="search-input header-search-input"
            />
            <button type="submit" className="search-button">Search</button>
          </form>
          <div className="header-stats">
            {transactions.length > 0 && <span className="table-count">Showing {transactions.length} records</span>}
          </div>
        </h3>
        {transactions.length > 0 ? (
          <div className="table-responsive">
            <table className="self-members-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Total Received Amount</th>
                  <th>No. of Received Amount</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, index) => (
                  <tr key={tx.id}>
                    <td data-label="S.No.">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td data-label="Date">{formatTransactionDate(tx.date, tx.transaction_date)}</td>
                    <td data-label="Amount">₹{tx.amount.toFixed(2)}</td>
                    <td data-label="Total Received Amount">₹{(tx.totalReceivedAmount ?? 0).toFixed(2)}</td>
                    <td data-label="No. of Received Amount">{tx.receivedAmountCount ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pagination-controls">
              <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
                &laquo; First
              </button>
              <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                &lsaquo; Previous
              </button>
              <span className="page-info">Page {currentPage} of {totalPages}</span>
              <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                Next &rsaquo;
              </button>
              <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
                Last &raquo;
              </button>
              <form onSubmit={handleGoToPageSubmit} className="go-to-page-form">
                <input
                  type="number"
                  value={goToPage}
                  onChange={(e) => setGoToPage(e.target.value)}
                  className="page-input"
                  min="1"
                  max={totalPages}
                  placeholder="Page"
                />
                <button type="submit">Go</button>
              </form>
              <div className="items-per-page-selector">
                <select value={itemsPerPage} onChange={e => setItemsPerPage(Number(e.target.value))} className="filter-select">
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                  <option value={1000}>1000</option>
                </select>
              </div>
            </div>
          </div>
        ) : (
          <p>No credit transactions found.</p>
        )}
      </div>
    );
  };

  const renderStats = () => {
    if (isStatsLoading) return <p>Loading stats...</p>;
    if (statsError && !counts) return <div className="error-message">{statsError}</div>;
    if (counts) {
      return (
        <div className="stats-grid">
          <div className="stat-card wallet-card">
            <div className="stat-icon">💰</div>
            <h3>Wallet Balance</h3>
            <p className="stat-number">₹{counts.walletBalance.toFixed(2)}</p>           
          </div>
          <div className="stat-card direct-card">
            <div className="stat-icon">👥</div>
            <h3>Direct</h3>
            <p className="stat-number">{counts.directReferrals}</p>
            <small>Direct Referrals</small>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="dashboard-container">
      {/* Component-specific styles for the admin view controls */}
      <style>{`
        .spent-text {
          color: #d9534f; /* A soft red color for debits/spent amounts */
        }
        .admin-view-controls {
          background-color: #e9ecef;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        .view-as-form {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .view-as-input {
          flex-grow: 1;
          border: 1px solid #ced4da;
          border-radius: 4px;
          padding: 10px 15px;
          font-size: 1rem;
        }
        .view-as-button {
          border: none;
          border-radius: 4px;
          padding: 10px 20px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          background-color: #007bff;
          color: white;
          transition: background-color 0.2s;
        }
        .view-as-button.clear-view-button {
          background-color: #6c757d;
        }
        .view-as-button:hover {
          opacity: 0.9;
        }
        .verification-message {
          padding: 8px 12px;
          border-radius: 4px;
          font-size: 0.9rem;
          font-weight: 500;
        }
        .verification-message.success {
          background-color: #d4edda; color: #155724;
        }
        .verification-message.error {
          background-color: #f8d7da; color: #721c24;
        }
        @media (max-width: 768px) {
          .view-as-form {
            flex-wrap: nowrap;
          }
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(5px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          animation: fadeIn 0.3s ease-out;
        }
        .modal-content {
          background: white;
          padding: 40px;
          border-radius: 20px;
          width: 90%;
          max-width: 350px;
          text-align: center;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
          animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .modal-title {
          font-size: 1.2rem;
          font-weight: 800;
          color: #0056b3;
          background-color: #e7f1ff;
          padding: 8px 16px;
          border-radius: 30px;
          display: inline-block;
          margin-bottom: 15px;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .modal-count {
          font-size: 5rem;
          font-weight: 800;
          color: #007bff;
          line-height: 1;
          margin: 10px 0 30px;
          text-shadow: 4px 4px 0px rgba(0,123,255,0.1);
        }
        .modal-close-btn {
          background-color: #007bff;
          color: white;
          border: none;
          padding: 12px 35px;
          border-radius: 50px;
          cursor: pointer;
          font-size: 1.1rem;
          font-weight: 600;
          box-shadow: 0 5px 15px rgba(0, 123, 255, 0.3);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .modal-close-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 123, 255, 0.4);
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
      {user?.role === 'admin' && (
        <div className="admin-view-controls">
          <form onSubmit={handleViewAsSubmit} className="view-as-form">
            <input
              type="text"
              value={viewAsSearchTerm}
              onChange={(e) => {
                setViewAsSearchTerm(e.target.value);
                setVerificationMessage(null); // Reset verification on change
              }}
              placeholder="Enter username to view their dashboard"
              className="view-as-input"
            />
            <button type="submit" className="view-as-button">View Dashboard</button>
            {viewAsUser && (
              <button type="button" onClick={handleClearViewAs} className="view-as-button clear-view-button">
                View My Dashboard
              </button>
            )}
          </form>
          {verificationMessage && (
            <div className={`verification-message ${verificationMessage.status}`} style={{marginTop: '10px', textAlign: 'left'}}>
              {verificationMessage.message}
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
        {viewAsUser && user?.role === 'admin' && counts ? (
          <h1 style={{ margin: 0 }}>Viewing Dashboard for: <strong>{counts.targetName || viewAsUser}</strong></h1>
        ) : (
          <h1 style={{ margin: 0 }}>Welcome, {user?.name || user?.username}!</h1>
        )}
      </div>
      {renderStats()}
      {renderTransactionsTable()}
    </div>
  );
};

export default DashboardPage;